import React from "react";
import { useIntl } from "react-intl";

import "./menu.scss";

import { useOuterClickEffect } from "../../hooks/useOuterClickEffect";
import { useTourContext } from "../tour/TourContext";
import { useFeedbackContext } from "../feedback/FeedbackContext";
import useStores from "../../hooks/useStores";
import { urlDecorator } from "../../utils/api";

import MenuButton from "./MenuButton";
import { LinkComponent, ButtonComponent } from "../header/HeaderButton";

import {
  handleDownloadPlanClick,
  handleDownloadCsvClick,
  handleDownloadImageClick,
} from "./utils";

import { ReactComponent as HelperSVG } from "../../assets/helper.svg";
import { ReactComponent as TourSVG } from "../../assets/tour-button-icon.svg";
import { ReactComponent as AvatarSVG } from "../../assets/avatar.svg";
import { ReactComponent as FeedbackSmallIconSVG } from "../../assets/feedback.svg";
import { ReactComponent as LockSVG } from "../../assets/lock.svg";
import { ReactComponent as UnlockSVG } from "../../assets/unlock.svg";
import { ReactComponent as CheckedSVG } from "../../assets/checked.svg";
import { ReactComponent as CameraSVG } from "../../assets/camera.svg";
import { ReactComponent as CsvSVG } from "../../assets/csv.svg";
import { ReactComponent as DPlanSVG } from "../../assets/dplan.svg";

import { availableStatuses } from "../../store/planFactory/injectPlanSerialization";

function MenuRow({ Icon, label, className, ...props }) {
  const Component = props.href == null ? ButtonComponent : LinkComponent;
  return (
    <Component
      {...props}
      className={`menu-row 
        ${className ?? ""}
      `}
    >
      <>
        {Icon && (
          <div className="menu-icon">
            <Icon />
          </div>
        )}
        {label && <span>{label}</span>}
      </>
    </Component>
  );
}

function Menu() {
  const menuRef = React.useRef(null);
  const [hasOpen, changeOpen] = React.useState(false);
  const intl = useIntl();

  const { uiState } = useStores();

  const {
    user,
    hasDev,
    plan,
    planId,
    settingsState,
    stepIdx: currentStep,
    stepIdxByName,
  } = uiState;

  const tourCtx = useTourContext();
  const feedbackCtx = useFeedbackContext();
  const labels = settingsState.texts.header;

  const closeMenuAction = () => {
    changeOpen(false);
  };

  useOuterClickEffect(menuRef, (event) => {
    if (!hasOpen) return;
    event.stopPropagation();
    closeMenuAction();
  });

  const menuClick = React.useCallback(
    (event) => {
      event.stopPropagation();
      changeOpen(true);
    },
    [changeOpen]
  );

  return (
    <>
      <MenuButton onClick={menuClick} />
      <div ref={menuRef} className={`menu ${hasOpen ? "opened" : ""}`}>
        {hasDev && (
          <MenuRow
            className="htitle"
            onClick={async () => {
              await handleDownloadImageClick(
                uiState.planId,
                uiState.user.email,
                plan,
                currentStep,
                uiState.calcApi
              );
              closeMenuAction();
            }}
            Icon={CameraSVG}
            label="Download Image"
          />
        )}

        {hasDev && (
          <MenuRow
            className="htitle"
            onClick={async () => {
              await handleDownloadPlanClick(plan);
              closeMenuAction();
            }}
            Icon={DPlanSVG}
            label="Download Plan"
          />
        )}

        {user.isAdmin && currentStep === stepIdxByName("recommendations") && (
          <MenuRow
            className="htitle"
            onClick={async () => {
              await handleDownloadCsvClick(
                intl,
                settingsState,
                plan.bomItems ?? [],
                uiState.planName,
                plan.pricesDictionary
              );
              closeMenuAction();
            }}
            Icon={CsvSVG}
            label="Download CSV"
          />
        )}

        {(availableStatuses.ToCheck === plan?.status ||
          availableStatuses.DuplicateToCheck === plan?.status) &&
          user.isAdmin &&
          currentStep === stepIdxByName("recommendations") && (
            <MenuRow
              onClick={async () => {
                await uiState.planChecked();
                closeMenuAction();
              }}
              Icon={CheckedSVG}
              label={intl.formatMessage({ id: labels.checkedLabel })}
            />
          )}
        <MenuRow
          href={
            settingsState
              ? urlDecorator(
                  intl.formatMessage({ id: settingsState.links.helperURL })
                )
              : "/"
          }
          onClick={() => {
            closeMenuAction();
          }}
          Icon={HelperSVG}
          label={
            labels ? intl.formatMessage({ id: labels.helperLabel }) : undefined
          }
          target="_blank"
          rel="noopener noreferrer"
        />

        {tourCtx.tourCanBeShown && (
          <MenuRow
            onClick={() => {
              uiState.clearSelectedElement();
              tourCtx.showTour();
              closeMenuAction();
            }}
            Icon={TourSVG}
            label={intl.formatMessage({ id: "tour.showTour.label" })}
          />
        )}
        <MenuRow
          onClick={() => {
            feedbackCtx.openFeedback();
            closeMenuAction();
          }}
          Icon={FeedbackSmallIconSVG}
          label={"Feedback"}
        />
        {
          //IPAT-412 Implement manual locking against unintended modification
          plan && (
            <MenuRow
              onClick={async () => {
                if (plan?.editable) {
                  plan.lock();
                } else {
                  plan.unlock();
                }
                await uiState.updatePlan();
                closeMenuAction();
              }}
              Icon={!plan?.editable ? LockSVG : UnlockSVG}
              label={intl.formatMessage({
                id: !plan?.editable ? labels.lockedLabel : labels.unlockedLabel,
              })}
            />
          )
        }
        {planId && (
          <div className="user-line">
            <MenuRow
              className="user"
              href={
                settingsState
                  ? urlDecorator(settingsState.links.logoutURL)
                  : "/"
              }
              onClick={() => {
                closeMenuAction();
              }}
              Icon={AvatarSVG}
              label={user?.name}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default Menu;
